import { api } from "@/lib/api";

// Web Push helpers — kept out of components so the UI doesn't have to think
// about VAPID / subscription plumbing.
//
// Flow when the user opts in:
//   1. Ask the browser for notification permission (blocks until user answers)
//   2. Ask the OS/browser push service for a Subscription bound to our VAPID key
//   3. Send that Subscription to /api/push/subscribe so the backend can push us
//
// Flow when they opt out:
//   1. Ask the service worker for its current subscription
//   2. Call subscription.unsubscribe() locally
//   3. DELETE /api/push/subscribe so the backend removes the row too

const urlBase64ToUint8Array = (base64String) => {
  // Convert URL-safe base64 (what VAPID uses) to the Uint8Array Push wants.
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  typeof window !== "undefined"
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

export const currentPermission = () =>
  isPushSupported() ? Notification.permission : "unsupported";

const ensureSwReady = async () => {
  if (!isPushSupported()) throw new Error("Push not supported on this device");
  // In dev the SW isn't auto-registered; register on demand so the opt-in flow
  // still works when you're testing in preview.
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js");
    // Wait for it to be active — required before subscribe() will succeed.
    await navigator.serviceWorker.ready;
    reg = await navigator.serviceWorker.getRegistration();
  }
  return reg;
};

export const subscribePush = async () => {
  if (!isPushSupported()) throw new Error("Push not supported on this device");
  // Ask permission FIRST so the browser prompt is tied to the user gesture.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    const err = new Error("Notification permission not granted");
    err.code = perm; // "denied" or "default"
    throw err;
  }
  const { data: cfg } = await api.get("/push/config");
  if (!cfg?.vapid_public_key) throw new Error("Push not configured on the server");

  const reg = await ensureSwReady();
  const existing = await reg.pushManager.getSubscription();
  let sub = existing;
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.vapid_public_key),
    });
  }
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return sub;
};

export const unsubscribePush = async () => {
  const reg = await ensureSwReady();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); }
  catch { /* backend may already have cleaned up — proceed anyway */ }
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  return true;
};

export const sendTestPush = async () => {
  const { data } = await api.post("/push/test");
  return data;
};
